import { Button, Col, Form, Row, Upload } from "antd";
import type { UploadFile, UploadProps, FormInstance } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { srmUploadService } from "@/utils/upload";
import { notifyError, notifySuccess } from "@/utils/notification";
import { FileListItem } from "@/components/FileListItem/FileListItem";
import { getFlowFileId } from "@/apis/flow.api";
import type { NamePath } from "antd/es/form/interface";

const onFileChange = (field: NamePath, form: FormInstance) => (info: any) => {
  // 处理多文件上传 - 检查所有文件的状态
  const doneFiles = info.fileList.filter((f: any) => f.status === "done");
  const errorFiles = info.fileList.filter((f: any) => f.status === "error");

  // 如果有文件上传完成
  if (doneFiles.length > 0) {
    form.setFieldValue(field, [
      ...info.fileList.map((f: any) => ({
        ...f,
        uid: f.response?.id || f.uid,
        url: f.response?.url || f.url,
      })),
    ]);
    // 只有当所有选中的文件都上传完成时才显示成功通知
    if (doneFiles.length === info.fileList.length) {
      notifySuccess(`成功上传${doneFiles.length}个文件`);
    }
  }

  // 处理上传错误
  if (errorFiles.length > 0) {
    errorFiles.forEach((errorFile: any) => {
      notifyError(`${errorFile.name} 文件上传失败`);
    });
  }
};

type MaterialFileFormFieldProps = {
  parentFieldName?: NamePath;
  fieldName: NamePath;
  label: string;
  required?: boolean;
  hiddenRemove?: boolean;
  bindFlow?: boolean;
  isCustomDisabled?: boolean;
  disabled?: boolean;
  accept?: string;
  maxCount?: number;
};

export function MaterialFileFormFields({
  parentFieldName,
  fieldName,
  label,
  required = true,
  hiddenRemove = false,
  bindFlow,
  isCustomDisabled,
  disabled,
  accept = ".pdf,.jpg,.jpeg,.png,.doc,.docx",
  maxCount = 5,
}: MaterialFileFormFieldProps) {
  const form = Form.useFormInstance();

  const uploadProps: UploadProps = {
    name: "file",
    showUploadList: false,
    accept,
    multiple: true, // 添加多文件选择支持
    maxCount,
    customRequest: async ({ file, onSuccess, onError }) => {
      try {
        const currentFiles = form.getFieldValue(fieldName);

        // 关键修复：只统计已成功上传的文件（过滤掉上传中/失败的）
        const successFiles = currentFiles.filter(
          (item: UploadFile) => item.status === "done", // 只算已上传完成的文件
        );

        // 获取当前文件列表数量（需要从组件外部传入）
        const currentFileCount = successFiles?.length || 0;

        // 上传前校验：检查文件数量是否超标
        if (currentFileCount >= maxCount) {
          notifyError("文件数量已达到上限");
          return;
        }

        const url = await srmUploadService.uploadFile(file as File);
        if (!bindFlow) {
          // 必须调用 onSuccess，否则 Upload 状态不会更新
          onSuccess?.({ url });
          return;
        }
        const res = await getFlowFileId({
          fileUrl: url,
          fileType: fieldName as string,
        });

        // 必须调用 onSuccess，否则 Upload 状态不会更新
        onSuccess?.({ url, id: res.data.data });
      } catch (err: any) {
        onError?.(err);
      }
    },
  };

  return (
    <Row>
      <Col span={24}>
        <Form.Item
          name={fieldName}
          label={label}
          valuePropName="fileList" // 必须
          getValueFromEvent={(e) => {
            return e.fileList.map((file: UploadFile) => {
              return {
                ...file,
                uid: file.response?.id || file.uid,
                url: file.response?.url || file.url,
              };
            });
          }}
          rules={
            required ? [{ required: true, message: `请上传${label}` }] : []
          }
          layout="horizontal"
        >
          {isCustomDisabled ? (
            <Upload
              {...uploadProps}
              onChange={onFileChange(fieldName, form)}
              disabled={disabled}
            >
              <Button
                icon={<UploadOutlined />}
                variant="outlined"
                color="primary"
                disabled={disabled}
              >
                点击上传
              </Button>
            </Upload>
          ) : (
            <Upload {...uploadProps} onChange={onFileChange(fieldName, form)}>
              <Button
                icon={<UploadOutlined />}
                variant="outlined"
                color="primary"
              >
                点击上传
              </Button>
            </Upload>
          )}
        </Form.Item>
      </Col>

      <Form.Item noStyle shouldUpdate>
        {({ setFieldValue }) => {
          const finalFieldName = parentFieldName
            ? [parentFieldName, ...fieldName]
            : fieldName;
          const fileList = form.getFieldValue(finalFieldName);
          if (!fileList) return null;

          const removeFile = (index: number, url?: string) => {
            setFieldValue(
              finalFieldName,
              fileList.filter(
                (p: any, pIndex: number) =>
                  `${p.url}-${pIndex}` !== `${url}-${index}`,
              ),
            );
          };

          return (
            <Col
              span={24}
              style={{
                marginBottom: fileList.length > 0 ? 12 : 0,
                marginTop: fileList.length > 0 ? -8 : 0,
              }}
            >
              {fileList.map((file: UploadFile, index: number) => (
                <FileListItem
                  key={file.uid || `${file.url}-${index}` || index}
                  file={file}
                  hiddenRemove={hiddenRemove}
                  remove={(url) => removeFile(index, url)}
                />
              ))}
            </Col>
          );
        }}
      </Form.Item>
    </Row>
  );
}
